import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { DatePicker, Input, InputNumber, Select, Tooltip, Icon } from "antd";
import TvrRepeater from "./tvr-repeater";

const { TextArea } = Input;
const { Option } = Select;

const DASH = "—";

// Date fields persist as ISO so the stored value stays parseable and sortable;
// the picker shows the analyst-facing format.
const DATE_VALUE_FORMAT = "YYYY-MM-DD";
const DATE_DISPLAY_FORMAT = "DD MMM YYYY";

// Currency fields store a RAW number (the API is typed) and only format for
// display — grouping and the ₹ symbol never reach the stored value.
const formatRupees = v =>
    v === "" || v === null || v === undefined
        ? ""
        : `₹ ${Number(v).toLocaleString("en-IN")}`;
const parseRupees = v => String(v).replace(/[^0-9.]/g, "");

/**
 * One Credit-TVR form field.
 *
 * The field is READ-ONLY until its subsection is put into edit mode (the pencil
 * on the subsection heading). While editing it is a fully controlled input: every
 * keystroke calls `onChange`, and the parent subsection holds the draft until the
 * analyst submits — nothing is written per-field.
 *
 * `edited` means UNSAVED — the draft differs from the stored value — so the amber
 * accent clears on a successful submit.
 */
class TvrField extends React.Component {
    // Controls speak strings; `null` is the empty value.
    stringValue() {
        const { value } = this.props;
        return value === null || value === undefined ? "" : String(value);
    }

    numberValue() {
        const raw = this.stringValue();
        if (raw === "") {
            return undefined;
        }
        const n = Number(raw);
        return Number.isNaN(n) ? undefined : n;
    }

    emit = value => this.props.onChange(value === undefined ? "" : value);

    // What the analyst sees when the section is locked.
    renderReadOnly() {
        const { type, suffix } = this.props;
        const raw = this.stringValue();
        if (raw === "") {
            return <div className={"cc-tvr-readonly cc-tvr-readonly--empty"}>{DASH}</div>;
        }
        let text = raw;
        if (type === "currency") {
            text = formatRupees(raw);
        } else if (type === "number") {
            text = suffix ? `${raw}${suffix}` : raw;
        } else if (type === "boolean") {
            text = raw === "true" ? "Yes" : "No";
        } else if (type === "date") {
            const m = moment(raw, DATE_VALUE_FORMAT);
            text = m.isValid() ? m.format(DATE_DISPLAY_FORMAT) : raw;
        }
        return <div className={"cc-tvr-readonly"}>{text}</div>;
    }

    renderControl() {
        const { type, options, min, max, suffix, saving } = this.props;
        if (type === "boolean") {
            // Tri-state: cleared reads as "not answered" (null), which is what
            // the API stores — distinct from an explicit "No".
            const raw = this.stringValue();
            return (
                <Select
                    className={"cc-tvr-input"}
                    value={raw === "" ? undefined : raw}
                    placeholder={"Select…"}
                    onChange={this.emit}
                    disabled={saving}
                    allowClear
                    style={{ width: "100%" }}
                >
                    <Option value={"true"}>{"Yes"}</Option>
                    <Option value={"false"}>{"No"}</Option>
                </Select>
            );
        }
        if (type === "currency") {
            return (
                <InputNumber
                    className={"cc-tvr-input"}
                    value={this.numberValue()}
                    placeholder={DASH}
                    disabled={saving}
                    min={0}
                    formatter={formatRupees}
                    parser={parseRupees}
                    style={{ width: "100%" }}
                    onChange={this.emit}
                />
            );
        }
        if (type === "number") {
            return (
                <InputNumber
                    className={"cc-tvr-input"}
                    value={this.numberValue()}
                    placeholder={DASH}
                    disabled={saving}
                    min={min}
                    max={max}
                    formatter={
                        suffix
                            ? v => (v === "" || v == null ? "" : `${v}${suffix}`)
                            : undefined
                    }
                    parser={
                        suffix ? v => String(v).replace(suffix, "") : undefined
                    }
                    style={{ width: "100%" }}
                    onChange={this.emit}
                />
            );
        }
        if (type === "date") {
            const parsed = this.stringValue()
                ? moment(this.stringValue(), DATE_VALUE_FORMAT)
                : null;
            return (
                <DatePicker
                    className={"cc-tvr-input"}
                    value={parsed && parsed.isValid() ? parsed : null}
                    format={DATE_DISPLAY_FORMAT}
                    placeholder={DASH}
                    disabled={saving}
                    allowClear
                    style={{ width: "100%" }}
                    onChange={date =>
                        this.emit(date ? date.format(DATE_VALUE_FORMAT) : "")
                    }
                />
            );
        }
        if (type === "select") {
            return (
                <Select
                    className={"cc-tvr-input"}
                    value={this.stringValue() || undefined}
                    placeholder={"Select…"}
                    onChange={this.emit}
                    disabled={saving}
                    allowClear
                    showSearch
                    optionFilterProp={"children"}
                    dropdownMatchSelectWidth={false}
                    style={{ width: "100%" }}
                >
                    {(options || []).map(opt => (
                        <Option key={opt} value={opt}>
                            {opt}
                        </Option>
                    ))}
                </Select>
            );
        }
        if (type === "combo") {
            // Pick one of the suggestions OR type a value that is not in the
            // list. antd v3's `combobox` mode is a text input with a dropdown, so
            // the committed value is always the raw string — the options are
            // suggestions, not a closed set.
            return (
                <Select
                    className={"cc-tvr-input"}
                    mode={"combobox"}
                    value={this.stringValue() || undefined}
                    placeholder={"Select or type…"}
                    onChange={this.emit}
                    disabled={saving}
                    filterOption={false}
                    dropdownMatchSelectWidth={false}
                    style={{ width: "100%" }}
                >
                    {(options || []).map(opt => (
                        <Option key={opt} value={opt}>
                            {opt}
                        </Option>
                    ))}
                </Select>
            );
        }
        if (type === "textarea") {
            return (
                <TextArea
                    className={"cc-tvr-input"}
                    value={this.stringValue()}
                    placeholder={DASH}
                    autosize={{ minRows: 2, maxRows: 6 }}
                    disabled={saving}
                    onChange={e => this.emit(e.target.value)}
                />
            );
        }
        return (
            <Input
                className={"cc-tvr-input"}
                value={this.stringValue()}
                placeholder={DASH}
                disabled={saving}
                onChange={e => this.emit(e.target.value)}
            />
        );
    }

    // Repeating groups own both their locked and editing rendering.
    renderList() {
        const { columns, value, editing, saving, onChange } = this.props;
        return (
            <TvrRepeater
                columns={columns || []}
                value={Array.isArray(value) ? value : []}
                editing={editing}
                saving={saving}
                onChange={onChange}
            />
        );
    }

    renderBody() {
        const { type, editing } = this.props;
        if (type === "list") {
            return this.renderList();
        }
        return editing ? this.renderControl() : this.renderReadOnly();
    }

    renderSuggestion() {
        const {
            suggestion,
            showSuggestion,
            suggestionBusy,
            onAcceptSuggestion,
            onRejectSuggestion
        } = this.props;
        if (!showSuggestion || !suggestion) {
            return null;
        }
        const busy = Boolean(suggestionBusy);
        const confidencePct = Math.round((suggestion.confidence || 0) * 100);
        const valueText = suggestion.value;
        const evidence = (suggestion.evidence || "").trim();

        return (
            <div className={"cc-tvr-suggestion"}>
                <Icon type={"bulb"} className={"cc-tvr-suggestion__bulb"} />
                <span className={"cc-tvr-suggestion__label"}>AI suggestion</span>
                <span className={"cc-tvr-suggestion__badge"}>
                    {confidencePct}% match
                </span>
                <Tooltip title={evidence || null}>
                    <span className={"cc-tvr-suggestion__value"}>{valueText}</span>
                </Tooltip>
                <span className={"cc-tvr-suggestion__actions"}>
                    <button
                        type={"button"}
                        className={
                            "cc-tvr-suggestion__icon-btn cc-tvr-suggestion__icon-btn--accept"
                        }
                        disabled={busy}
                        title={"Fill this field in the form (saved on Submit)"}
                        aria-label={"Accept suggestion"}
                        onClick={onAcceptSuggestion}
                    >
                        <Icon type={busy ? "loading" : "check"} />
                    </button>
                    <button
                        type={"button"}
                        className={
                            "cc-tvr-suggestion__icon-btn cc-tvr-suggestion__icon-btn--reject"
                        }
                        disabled={busy}
                        title={"Reject suggestion"}
                        aria-label={"Reject suggestion"}
                        onClick={onRejectSuggestion}
                    >
                        <Icon type={"close"} />
                    </button>
                </span>
            </div>
        );
    }

    render() {
        const { label, type, edited, wide } = this.props;

        return (
            <div
                className={
                    "cc-tvr-field" +
                    (type === "textarea" || type === "list" || wide
                        ? " cc-tvr-field--wide"
                        : "") +
                    (edited ? " cc-tvr-field--edited" : "")
                }
            >
                <div className={"cc-tvr-field__head"}>
                    <span className={"cc-tvr-field__label"}>
                        {label}
                        {/* Every TVR field is mandatory. */}
                        <span className={"cc-tvr-required"} title={"Mandatory"}>
                            {"*"}
                        </span>
                        {edited ? (
                            <Tooltip title={"Unsaved change"}>
                                <span className={"cc-tvr-dot"} />
                            </Tooltip>
                        ) : null}
                    </span>
                </div>
                {this.renderSuggestion()}
                {this.renderBody()}
            </div>
        );
    }
}

TvrField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array
    ]),
    // True while this field's draft differs from its stored value (unsaved).
    edited: PropTypes.bool,
    type: PropTypes.oneOf([
        "text",
        "textarea",
        "select",
        "combo",
        "date",
        "number",
        "currency",
        "boolean",
        "list"
    ]),
    options: PropTypes.array,
    // Column config for `list` fields — see TvrRepeater.
    columns: PropTypes.array,
    min: PropTypes.number,
    max: PropTypes.number,
    suffix: PropTypes.string,
    wide: PropTypes.bool,
    // True while the parent subsection is in edit mode.
    editing: PropTypes.bool,
    // True while the parent subsection's submit is in flight.
    saving: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    suggestion: PropTypes.shape({
        value: PropTypes.string,
        confidence: PropTypes.number,
        evidence: PropTypes.string
    }),
    showSuggestion: PropTypes.bool,
    suggestionBusy: PropTypes.bool,
    onAcceptSuggestion: PropTypes.func,
    onRejectSuggestion: PropTypes.func
};

export default TvrField;
